import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import './index.scss';
import { saveCookieForShopify, saveAICookieForShopify } from '@elem/ui-common/utils/CookieForShopify';
import gtm from '@/utils/gtm';
import getLocal from '@/app/utils/getLocal';

export interface EfProductNaverItemProps {
    key: string;
    text: string;
    url?: string;
    href?: string;
}

export interface EfProductNaverProps {
    className?: string;
    /**
     * theme主题
     */
    theme?: 'black' | 'white';
    /**
     * 当前处于激活态的tab的key值
     */
    activeTab?: string;
    /**
     * 产品名/标题
     */
    title?: string;
    hideBuy?: boolean;
    /**
     * buy 配置
     */
    buyItem?: EfProductNaverItemProps;
    /**
     * 点击tab时，不触发setCurTab
     */
    hideClickFnTabs?: string[];
    /**
     * tab列表
     */
    tabs?: EfProductNaverItemProps[];
    /**
     * 点击tab回调
     */
    callbackTab?: any;
    /**
     * 点击购买回调
     */
    callbackBuy?: any;
    buyNowGtm?: any;
    productHighlightIt?: boolean;
}

/**
 * 维护: kalkin
 *
 * 产品导航 - [Buy Now]按钮支持三种情况：
 * 1. 1.管理后台产品页配置了BuyLink, 且BuyLink为当前产品详情页的锚点 => 跳转回product详情页tab中定位到锚点处(_self本页面打开)
 * 2. 2.管理后台产品页配置了BuyLink, 且BuyLink不为当前产品url所在页面 => 跳转指定url页面（_blank新页面打开）
 * 3. 3.若管理后台未配置BuyLink，则buy now按钮默认跳转至bundle所在的tab中（bundle有可能不显示在tab列表中 - jp站常见）
 */
const EfProductNaver: React.FC<EfProductNaverProps> = ({
    className = '',
    theme = 'white',
    activeTab = 'Product',
    title,
    hideBuy = false,
    buyItem = {
        key: 'Buy',
        text: 'Buy Now',
    },
    hideClickFnTabs = [''],
    productHighlightIt = false,
    tabs = [],
    callbackTab,
    callbackBuy,
    buyNowGtm,
}) => {
    const prefixCls = `ef-product-naver`;
    const prefix = `${prefixCls}-`;
    const classes = classNames(prefixCls, {
        [`${prefixCls}-${theme}`]: theme,
        [className]: className,
    });

    const [curTab, setCurTab] = useState(activeTab);
    const { locale = 'us' } = getLocal();

    const handleClickTab = (tab: EfProductNaverItemProps, idx: number) => {
        if (callbackTab) {
            callbackTab(tab);
        }
        gtm.push({
            event: 'uaEvent',
            event_name: 'product_navigation',
            navigation_name: tab.text,
            product_name: title,

            __btn_id1: `ef-naver-content .tab-item-${idx + 1}`,
            __btn_id2: `naver-tab-item-${idx + 1}`,
            __btn_type: 'common',
        });
        if (hideClickFnTabs.includes(tab.key)) {
            return;
        }
        setCurTab(tab.key);
        window.scrollTo({
            top: 0,
            // behavior: 'smooth',
        });
    };

    // product 页面滚动锚点高亮
    useEffect(() => {
        if (!productHighlightIt) return;
        const notUseTabs = tabs.filter((i) => !hideClickFnTabs.includes(i.key) && i.key !== 'Product').map((i) => i.key);
        if (notUseTabs.includes(curTab)) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (notUseTabs.includes(curTab)) return;
                const tabKey = entry.target.getAttribute('data-tab-key');
                setCurTab(entry.isIntersecting ? (tabKey as string) : 'Product');
            });
        });
        tabs.filter((i) => i.href).forEach((tab) => {
            const el = document.querySelector(tab.href as string);
            if (el) {
                el.setAttribute('data-tab-key', tab.key);
                observer.observe(el);
            }
        });
        return () => {
            observer.disconnect();
        };
    }, [curTab, tabs, productHighlightIt, hideClickFnTabs]);

    const handleBuy = () => {
        try {
            if (buyItem?.url && buyItem?.key === 'Buy') {
                saveAICookieForShopify(buyItem?.url, locale);
            }

            if (buyNowGtm) {
                gtm.push({
                    __btn_id1: `ef-naver-content .tab-buy`,
                    __btn_id2: 'naver-tab-buy',
                    __btn_type: 'common',
                    ...buyNowGtm,
                });
            } else {
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'jump_button',
                    position: 'Top',
                    button_name: buyItem.text,
                    url: buyItem?.url || 'none',

                    __btn_id1: `ef-naver-content .tab-buy`,
                    __btn_id2: 'naver-tab-buy',
                    __btn_type: 'common',
                });
            }
        } catch (e) {
            console.error(e);
        } finally {
            if (buyItem?.url && buyItem?.key === 'Buy') {
                window.open(
                    saveCookieForShopify(buyItem?.url, locale) || buyItem?.url,
                    buyItem.url.indexOf(window.location.pathname) > -1 ? '_self' : '_blank',
                );
            } else if (buyItem?.key !== 'Buy') {
                setCurTab(buyItem.key);
            }
            if (callbackBuy) {
                callbackBuy();
            }
        }
    };

    useEffect(() => {
        setCurTab(activeTab);
    }, [activeTab]);

    return (
        <div className={classes}>
            <div className={`${prefix}inner ef-naver-content`}>
                <p className={`${prefix}title`}>{title}</p>
                <ul className={`${prefix}tabs`}>
                    {tabs?.map((tab: EfProductNaverItemProps, k: number) => (
                        <li
                            className={`${prefix}tab${tab?.key === curTab ? ` ${prefix}tab-active` : ''} GtmModuleData-btn-common tab-item-${k + 1}`}
                            key={k}
                            data-btn-id={`naver-tab-item-${k + 1}`}
                            onClick={() => handleClickTab(tab, k)}
                        >
                            {tab.text}
                        </li>
                    ))}
                </ul>
                {buyItem && !hideBuy && (
                    <span className={`${prefix}buy GtmModuleData-btn-common tab-buy`} onClick={handleBuy} data-btn-id="naver-tab-buy">
                        {buyItem.text}
                    </span>
                )}
            </div>
        </div>
    );
};

export default EfProductNaver;
